import { useState, useEffect, useCallback } from 'react';
import { authService } from '../api/services';
import socketService from '../api/socket';

/**
 * Hook personnalisé pour gérer l'authentification
 */
export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Charger l'utilisateur depuis le localStorage au montage
  useEffect(() => {
    const loadUser = () => {
      const currentUser = authService.getCurrentUser();
      const authenticated = authService.isAuthenticated();
      
      setUser(currentUser);
      setIsAuthenticated(authenticated);
      setLoading(false);

      // Connecter le WebSocket si authentifié
      if (authenticated) {
        const token = localStorage.getItem('accessToken');
        socketService.connect(token);
      }
    };

    loadUser();
  }, []);

  /**
   * Connexion
   */
  const login = useCallback(async (credentials) => {
    try {
      setLoading(true);
      const response = await authService.login(credentials);
      
      if (response.success) {
        setUser(response.data.user);
        setIsAuthenticated(true);
        
        // Connecter le WebSocket
        socketService.connect(response.data.tokens.accessToken);
      }
      
      return response;
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Inscription
   */
  const register = useCallback(async (userData) => {
    try {
      setLoading(true);
      const response = await authService.register(userData);
      
      if (response.success) {
        setUser(response.data.user);
        setIsAuthenticated(true);
        
        // Connecter le WebSocket
        socketService.connect(response.data.tokens.accessToken);
      }
      
      return response;
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Déconnexion
   */
  const logout = useCallback(async () => {
    try {
      setLoading(true);
      await authService.logout();
      
      setUser(null);
      setIsAuthenticated(false);
      
      // Déconnecter le WebSocket
      socketService.disconnect();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Mettre à jour l'utilisateur
   */
  const updateUser = useCallback((updatedUser) => {
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  }, []);

  return {
    user,
    loading,
    isAuthenticated,
    login,
    register,
    logout,
    updateUser,
  };
};

export default useAuth;
